const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";

// Helper function to get auth token
const getAuthToken = () => {
  return localStorage.getItem("authToken");
};

// Helper function to make authenticated requests
const makeAuthenticatedRequest = async (url, options = {}) => {
  const token = getAuthToken();
  const headers = {
    "Content-Type": "application/json",
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  } else {
    // No token available, throw error
    throw new Error("No authentication token available");
  }

  const response = await fetch(`${API_BASE_URL}${url}`, {
    ...options,
    headers,
  });

  if (response.status === 401) {
    // Token expired or invalid, redirect to login
    localStorage.removeItem("authToken");
    localStorage.removeItem("currentUser");
    const error = new Error("Authentication failed");
    error.status = 401;
    throw error;
  }

  if (!response.ok) {
    const error = new Error(`HTTP ${response.status}: ${response.statusText}`);
    error.status = response.status;
    throw error;
  }

  return response;
};

// API service object
export const apiService = {
  // Auth endpoints
  auth: {
    login: async (email, password) => {
      const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, password }),
      });
      return response.json();
    },

    register: async (userData) => {
      const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(userData),
      });
      return response.json();
    },

    getCurrentUser: async () => {
      const response = await makeAuthenticatedRequest("/api/auth/me");
      return response ? response.json() : null;
    },

    refreshToken: async () => {
      const response = await makeAuthenticatedRequest("/api/auth/refresh", {
        method: "POST",
      });
      return response ? response.json() : null;
    },

    changePassword: async (passwordData) => {
      try {
        const response = await makeAuthenticatedRequest(
          "/api/auth/change-password",
          {
            method: "POST",
            body: JSON.stringify(passwordData),
          },
        );
        return response ? response.json() : null;
      } catch (error) {
        console.warn("Change password API not available:", error.message);
        return { success: false, error: error.message };
      }
    },
  },

  // User endpoints
  users: {
    getProfile: async () => {
      const response = await makeAuthenticatedRequest("/api/users/profile");
      return response ? response.json() : null;
    },

    updateProfile: async (userData) => {
      const response = await makeAuthenticatedRequest("/api/users/profile", {
        method: "PUT",
        body: JSON.stringify(userData),
      });
      return response ? response.json() : null;
    },

    updateSettings: async (settings) => {
      try {
        const response = await makeAuthenticatedRequest("/api/users/settings", {
          method: "PUT",
          body: JSON.stringify(settings),
        });
        return response ? response.json() : null;
      } catch (error) {
        console.warn("Settings API not available:", error.message);
        return { success: false, error: error.message };
      }
    },

    getAllUsers: async (page = 1, limit = 10) => {
      const response = await makeAuthenticatedRequest(
        `/api/users?page=${page}&limit=${limit}`,
      );
      return response ? response.json() : null;
    },
  },

  // Learning paths and courses
  courses: {
    getLearningPaths: async () => {
      const response = await fetch(
        `${API_BASE_URL}/api/courses/learning-paths`,
      );
      return response.json();
    },

    getLearningPath: async (id) => {
      const response = await fetch(
        `${API_BASE_URL}/api/courses/learning-paths/${id}`,
      );
      return response.json();
    },

    getCourse: async (id) => {
      const response = await fetch(`${API_BASE_URL}/api/courses/${id}`);
      return response.json();
    },

    createLearningPath: async (pathData) => {
      const response = await makeAuthenticatedRequest(
        "/api/courses/learning-paths",
        {
          method: "POST",
          body: JSON.stringify(pathData),
        },
      );
      return response ? response.json() : null;
    },

    createCourse: async (courseData) => {
      const response = await makeAuthenticatedRequest("/api/courses", {
        method: "POST",
        body: JSON.stringify(courseData),
      });
      return response ? response.json() : null;
    },
  },

  // Learning resources
  resources: {
    getAll: async (filters = {}) => {
      const params = new URLSearchParams();
      if (filters.skill) params.append("skill", filters.skill);
      if (filters.type) params.append("type", filters.type);
      if (filters.difficulty) params.append("difficulty", filters.difficulty);
      if (filters.search) params.append("search", filters.search);
      if (filters.limit) params.append("limit", filters.limit);
      if (filters.offset) params.append("offset", filters.offset);

      const response = await fetch(
        `${API_BASE_URL}/api/resources?${params.toString()}`,
      );
      return response.json();
    },

    getById: async (id) => {
      const response = await fetch(`${API_BASE_URL}/api/resources/${id}`);
      return response.json();
    },

    getBySkill: async (skillId, limit = 10) => {
      const response = await fetch(
        `${API_BASE_URL}/api/resources/skill/${skillId}?limit=${limit}`,
      );
      return response.json();
    },

    create: async (resourceData) => {
      const response = await makeAuthenticatedRequest("/api/resources", {
        method: "POST",
        body: JSON.stringify(resourceData),
      });
      return response ? response.json() : null;
    },

    update: async (id, resourceData) => {
      const response = await makeAuthenticatedRequest(`/api/resources/${id}`, {
        method: "PUT",
        body: JSON.stringify(resourceData),
      });
      return response ? response.json() : null;
    },

    delete: async (id) => {
      const response = await makeAuthenticatedRequest(`/api/resources/${id}`, {
        method: "DELETE",
      });
      return response ? response.json() : null;
    },

    getStats: async () => {
      const response = await makeAuthenticatedRequest(
        "/api/resources/stats/summary",
      );
      return response ? response.json() : null;
    },
  },

  // Assessments
  assessments: {
    // Get all assessments
    getAllAssessments: async () => {
      const response = await makeAuthenticatedRequest("/api/assessments");
      return response ? response.json() : null;
    },

    // Get assessments by skill
    getSkillAssessments: async (skillId) => {
      const response = await makeAuthenticatedRequest(
        `/api/assessments/skill/${skillId}`,
      );
      return response ? response.json() : null;
    },

    // Get available skills
    getSkills: async () => {
      const response = await makeAuthenticatedRequest(
        "/api/assessments/skills",
      );
      return response ? response.json() : null;
    },

    // Get single assessment with questions
    getAssessment: async (id) => {
      const response = await makeAuthenticatedRequest(`/api/assessments/${id}`);
      return response ? response.json() : null;
    },

    // Submit assessment attempt
    submitAssessment: async (id, answers, timingData = {}) => {
      const response = await makeAuthenticatedRequest(
        `/api/assessments/${id}/submit`,
        {
          method: "POST",
          body: JSON.stringify({
            answers,
            startedAt: timingData.startedAt,
            duration: timingData.duration,
          }),
        },
      );
      return response ? response.json() : null;
    },

    // Get assessment report
    getAssessmentReport: async (attemptId) => {
      const response = await makeAuthenticatedRequest(
        `/api/assessments/report/${attemptId}`,
      );
      return response ? response.json() : null;
    },

    // Get user's assessment attempts
    getAssessmentAttempts: async (id) => {
      const response = await makeAuthenticatedRequest(
        `/api/assessments/${id}/attempts`,
      );
      return response ? response.json() : null;
    },

    // Create new assessment (admin/instructor only)
    createAssessment: async (assessmentData) => {
      const response = await makeAuthenticatedRequest("/api/assessments", {
        method: "POST",
        body: JSON.stringify(assessmentData),
      });
      return response ? response.json() : null;
    },

    // Update existing assessment (admin/instructor only)
    updateAssessment: async (assessmentId, assessmentData) => {
      try {
        console.log("Updating assessment:", assessmentId, assessmentData);

        const response = await makeAuthenticatedRequest(
          `/api/assessments/${assessmentId}`,
          {
            method: "PUT",
            body: JSON.stringify(assessmentData),
          },
        );

        if (!response) {
          throw new Error("No response received");
        }

        console.log("Response status:", response.status);

        if (!response.ok) {
          const errorData = await response.text();
          console.error("Error response:", errorData);
          throw new Error(`HTTP ${response.status}: ${errorData}`);
        }

        const result = await response.json();
        console.log("Update result:", result);
        return result;
      } catch (error) {
        console.error("Update assessment error:", error);
        throw error;
      }
    },

    // Delete assessment (admin/instructor only)
    deleteAssessment: async (assessmentId) => {
      const response = await makeAuthenticatedRequest(
        `/api/assessments/${assessmentId}`,
        {
          method: "DELETE",
        },
      );
      return response ? response.json() : null;
    },

    // Get top assessments with statistics (admin only)
    getTopAssessments: async (limit = 5) => {
      const response = await makeAuthenticatedRequest(
        `/api/assessments/stats/top?limit=${limit}`,
      );
      return response ? response.json() : null;
    },

    // Test endpoint for debugging
    testStatsEndpoint: async () => {
      const response = await makeAuthenticatedRequest(
        `/api/assessments/test/stats`,
      );
      return response ? response.json() : null;
    },

    // Legacy endpoint for backward compatibility (deprecated)
    getCourseAssessments: async (courseId) => {
      console.warn(
        "getCourseAssessments is deprecated. Use getSkillAssessments instead.",
      );
      const response = await makeAuthenticatedRequest(
        `/api/assessments/course/${courseId}`,
      );
      return response ? response.json() : null;
    },
  },

  // Progress tracking
  progress: {
    getDashboard: async () => {
      const response = await makeAuthenticatedRequest(
        "/api/progress/dashboard",
      );
      return response ? response.json() : null;
    },

    getRecommendedAssessments: async (limit = 4, category = null) => {
      let url = `/api/progress/recommended-assessments?limit=${limit}`;
      if (category) {
        url += `&category=${encodeURIComponent(category)}`;
      }
      const response = await makeAuthenticatedRequest(url);
      return response ? response.json() : null;
    },

    enrollInLearningPath: async (learningPathId) => {
      const response = await makeAuthenticatedRequest(
        `/api/progress/enroll/${learningPathId}`,
        {
          method: "POST",
        },
      );
      return response ? response.json() : null;
    },

    updateCourseProgress: async (courseId, progressData) => {
      const response = await makeAuthenticatedRequest(
        `/api/progress/course/${courseId}`,
        {
          method: "PUT",
          body: JSON.stringify(progressData),
        },
      );
      return response ? response.json() : null;
    },

    getCourseProgress: async (courseId) => {
      const response = await makeAuthenticatedRequest(
        `/api/progress/course/${courseId}`,
      );
      return response ? response.json() : null;
    },
  },

  // Health check
  health: {
    basic: async () => {
      const response = await fetch(`${API_BASE_URL}/health`);
      return response.json();
    },

    detailed: async () => {
      const response = await fetch(`${API_BASE_URL}/health/detailed`);
      return response.json();
    },

    getPublicStats: async () => {
      const response = await fetch(`${API_BASE_URL}/health/stats`);
      return response.json();
    },
  },

  // Admin endpoints
  admin: {
    getRecentActivity: async (limit = 10) => {
      const response = await makeAuthenticatedRequest(
        `/api/assessments/admin/activity?limit=${limit}`,
      );
      return response ? response.json() : null;
    },

    getStats: async () => {
      const response = await makeAuthenticatedRequest(
        "/api/assessments/admin/stats",
      );
      return response ? response.json() : null;
    },

    // Analytics endpoints
    getAnalyticsOverview: async (timeframe = "last_30_days") => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/admin/analytics/overview?timeframe=${timeframe}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.warn(
          "Analytics overview endpoint not available:",
          error.message,
        );
        return { success: false, error: error.message };
      }
    },

    getUserGrowthData: async (timeframe = "last_30_days") => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/admin/analytics/user-growth?timeframe=${timeframe}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.warn("User growth data endpoint not available:", error.message);
        return { success: false, error: error.message };
      }
    },

    getAssessmentAnalytics: async (timeframe = "last_30_days") => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/admin/analytics/assessments?timeframe=${timeframe}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.warn(
          "Assessment analytics endpoint not available:",
          error.message,
        );
        return { success: false, error: error.message };
      }
    },

    getSkillPerformanceData: async (timeframe = "last_30_days") => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/admin/analytics/skills?timeframe=${timeframe}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.warn(
          "Skill performance endpoint not available:",
          error.message,
        );
        return { success: false, error: error.message };
      }
    },

    getUserActivityData: async (timeframe = "last_30_days") => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/admin/analytics/user-activity?timeframe=${timeframe}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.warn("User activity endpoint not available:", error.message);
        return { success: false, error: error.message };
      }
    },

    getGeographicData: async (timeframe = "last_30_days") => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/admin/analytics/geographic?timeframe=${timeframe}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.warn("Geographic data endpoint not available:", error.message);
        return { success: false, error: error.message };
      }
    },

    exportAnalyticsReport: async (
      timeframe = "last_30_days",
      format = "csv",
    ) => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/admin/analytics/export?timeframe=${timeframe}&format=${format}`,
        );
        return response ? response.blob() : null;
      } catch (error) {
        console.warn("Export analytics endpoint not available:", error.message);
        return null;
      }
    },

    // User management
    deleteUser: async (userId) => {
      const response = await makeAuthenticatedRequest(
        `/api/admin/users/${userId}`,
        {
          method: "DELETE",
        },
      );
      return response ? response.json() : null;
    },
  },

  // ML-Powered Recommendations
  mlRecommendations: {
    // Get personalized learning path with ML recommendations
    getLearningPath: async (limit = 20) => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/ml-recommendations/learning-path?limit=${limit}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.error("ML learning path error:", error);
        return { success: false, error: error.message };
      }
    },

    // Get ML-enhanced skill gap analysis
    getSkillGaps: async () => {
      try {
        const response = await makeAuthenticatedRequest(
          "/api/ml-recommendations/skill-gaps",
        );
        return response ? response.json() : null;
      } catch (error) {
        console.error("ML skill gaps error:", error);
        return { success: false, error: error.message };
      }
    },

    // Get quick recommendations for dashboard
    getQuickRecommendations: async (limit = 5) => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/ml-recommendations/quick?limit=${limit}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.error("Quick recommendations error:", error);
        return { success: false, error: error.message };
      }
    },

    // Get skill-specific learning path
    getSkillLearningPath: async (skillId) => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/ml-recommendations/skill/${skillId}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.error("Skill learning path error:", error);
        return { success: false, error: error.message };
      }
    },

    // Get learning path summary
    getSummary: async () => {
      try {
        const response = await makeAuthenticatedRequest(
          "/api/ml-recommendations/summary",
        );
        return response ? response.json() : null;
      } catch (error) {
        console.error("Learning path summary error:", error);
        return { success: false, error: error.message };
      }
    },
  },

  // Leaderboard
  leaderboard: {
    // Get leaderboard data
    getLeaderboard: async (category = "overall", timeframe = "all-time") => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/leaderboard?category=${category}&timeframe=${timeframe}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.error("Get leaderboard error:", error);
        return { success: false, error: error.message };
      }
    },

    // Get user rank
    getUserRank: async (userId, category = "overall") => {
      try {
        const response = await makeAuthenticatedRequest(
          `/api/leaderboard/user/${userId}?category=${category}`,
        );
        return response ? response.json() : null;
      } catch (error) {
        console.error("Get user rank error:", error);
        return { success: false, error: error.message };
      }
    },

    // Get leaderboard statistics
    getStats: async () => {
      try {
        const response = await makeAuthenticatedRequest(
          "/api/leaderboard/stats",
        );
        return response ? response.json() : null;
      } catch (error) {
        console.error("Get leaderboard stats error:", error);
        return { success: false, error: error.message };
      }
    },
  },
};

export default apiService;
